import React, { useCallback, useRef } from 'react';
import noop from 'lodash/noop';
import isFunction from 'lodash/isFunction';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import './Slider.scss';

export default function Slider({
  min,
  max,
  step,
  labelFunction,
  value,
  onChange,
  showTooltip,
  legend,
  ariaLabel,
  ariaDescribedBy,
  className,
}) {
  const track = useRef();
  const thumb = useRef();
  const previousValue = useRef(); // required during mouse dragging to get prev value for preventing extra rerenders

  const percent = ((value - min) / (max - min)) * 100;

  const syncValue = (targetValue) => {
    let newValue = Math.round(targetValue / step) * step;
    if (newValue > max) {
      newValue = max;
    }
    if (newValue < min) {
      newValue = min;
    }
    if (newValue !== previousValue.current) {
      previousValue.current = newValue;
      onChange(newValue);
    }
  };

  const handleThumbMove = (ev) => {
    const x = ev?.clientX || ev?.changedTouches?.[0]?.clientX;

    // Using the track and mouse/touch position to calculate the new value.
    const rect = track.current?.getBoundingClientRect();
    syncValue(min + ((max - min) * (x - rect.left)) / rect.width);
  };

  const handleThumbEnd = () => {
    // Attach new event handlers on the document, so the slider
    // keeps working event when the users leaves the slider element.
    document.removeEventListener('mousemove', handleThumbMove);
    document.removeEventListener('mouseup', handleThumbEnd);

    // Attach new event handlers.
    document.removeEventListener('touchmove', handleThumbMove);
    document.removeEventListener('touchend', handleThumbEnd);
  };

  const handleThumbStart = (ev) => {
    ev.preventDefault();

    // Make sure there aren't any lingering event handlers.
    handleThumbEnd();

    // Attach new mouse event handlers on the document, so the slider
    // keeps working event when the mouse pointer leaves the slider
    // track element.
    document.addEventListener('mousemove', handleThumbMove);
    document.addEventListener('mouseup', handleThumbEnd);
    // Attach new touch event handlers.
    document.addEventListener('touchmove', handleThumbMove);
    document.addEventListener('touchend', handleThumbEnd);

    thumb.current?.focus();

    // Let's move the focussed thumb to the new location
    handleThumbMove(ev);
  };

  const handleKeyDown = (ev) => {
    // No modifier keys
    if (ev.ctrlKey || ev.shiftKey || ev.altKey || ev.metaKey) return;
    // No IME keys
    if (ev.isComposing || ev.keyCode === 229) return;

    const keyCode = ev.which || ev.keyCode;
    let newValue;

    // Page up/down jump about 10% of the full range.
    const page = Math.max(step, (max - min) * 0.1);

    if (keyCode === 33) {
      // PAGE_UP
      newValue = value + page;
    } else if (keyCode === 34) {
      // PAGE_DOWN
      newValue = value - page;
    } else if (keyCode === 35) {
      // END
      newValue = max;
    } else if (keyCode === 36) {
      // HOME
      newValue = min;
    } else if (keyCode === 37 || keyCode === 40) {
      // LEFT/DOWN
      newValue = value - step;
    } else if (keyCode === 39 || keyCode === 38) {
      // RIGHT/UP
      newValue = value + step;
    } else {
      return;
    }

    // We don't want the page start scrolling.
    ev.preventDefault();

    syncValue(newValue);
  };

  const getLabel = useCallback(
    (currentValue) => {
      if (labelFunction && isFunction(labelFunction)) {
        return labelFunction(currentValue);
      }
      return currentValue;
    },
    [labelFunction]
  );

  return (
    <fieldset
      className={classnames('rx-slider', {
        [className]: className,
      })}>
      {legend && <legend className="es-slider-label">{legend}</legend>}
      <input
        aria-label={ariaLabel}
        className="es-slider-input"
        max={max}
        min={min}
        name="slider-tooltip-1"
        step={step}
        type="range"
        value={value}
        onChange={() => noop()}
      />
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className="es-slider-track" ref={track} onMouseDown={handleThumbStart} onTouchStart={handleThumbStart}>
        <span className="es-slider-selected" style={{ right: `${100 - percent}%` }} />
        <button
          data-e2e="slider-thumb"
          ref={thumb}
          type="button"
          className="es-slider-thumb"
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          role="slider"
          aria-valuenow={getLabel(value)}
          style={{ left: `${percent}%` }}
          onKeyDown={handleKeyDown}>
          {showTooltip && (
            <span
              className={classnames('es-slider-thumb-tooltip', {
                'show-right': percent < 50,
              })}>
              {getLabel(value)}
            </span>
          )}
        </button>
      </div>
    </fieldset>
  );
}

Slider.defaultProps = {
  showTooltip: true,
};

Slider.propTypes = {
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  labelFunction: PropTypes.func,
  showTooltip: PropTypes.bool,
  legend: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  className: PropTypes.string,
};
